// pages/VendorPurchasePage.jsx
import React, { useState } from "react";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

const VendorPurchasePage = () => {
  const { isGhana, config } = useCountry();

  const [form, setForm] = useState({ amount: "", source: "bank" });
  const [message, setMessage] = useState("");
  const [adminBank, setAdminBank] = useState(null);
  const [loading, setLoading] = useState(false);

  // 🎨 THEME PER COUNTRY
  const theme = isGhana
    ? {
        bgColor: "bg-[#0a0500]",
        cardBg: "bg-[#1a0a00]",
        inputBg: "bg-[#2a0a00]",
        titleColor: "text-yellow-400",
        submitBtn: "bg-gradient-to-r from-yellow-500 to-red-600 hover:from-yellow-600 hover:to-red-700",
        adminBoxAccent: "border-yellow-500/20",
        currencySymbol: "₵",
        flag: "🇬🇭",
        sources: [
          { value: "bank", label: "Bank Transfer" },
          { value: "mtn", label: "📱 MTN Mobile Money" },
          { value: "telecel", label: "💳 Telecel Cash" },
          { value: "airteltigo", label: "💰 AirtelTigo Money" },
          { value: "gcb", label: "🏦 GCB Bank" },
        ],
      }
    : {
        bgColor: "bg-gray-900",
        cardBg: "bg-gray-800",
        inputBg: "bg-gray-700",
        titleColor: "text-white",
        submitBtn: "bg-purple-600 hover:bg-purple-700",
        adminBoxAccent: "border-gray-700",
        currencySymbol: "₦",
        flag: "🇳🇬",
        sources: [
          { value: "bank", label: "Bank Transfer" },
          { value: "opay", label: "OPay" },
          { value: "kuda", label: "Kuda" },
          { value: "palmpay", label: "PalmPay" },
          { value: "moniepoint", label: "MoniePoint" },
        ],
      };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const res = await API.post("/vendors/purchase", {
        ...form,
        country: isGhana ? "GH" : "NG",
        currency: isGhana ? "GHS" : "NGN",
      });
      setMessage("✅ Purchase request sent to Admin!");
      setAdminBank(res.data?.adminBank || null);
    } catch (err) {
      setMessage("❌ Failed to send request.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage("✅ Copied to clipboard!");
    setTimeout(() => setMessage(""), 2000);
  };

  return (
    <div className={`min-h-screen ${theme.bgColor} text-white p-6`}>

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      <h1 className={`text-2xl font-bold mb-1 ${theme.titleColor}`}>Purchase from Admin</h1>
      <p className="text-xs text-gray-400 mb-6">
        {theme.flag} {config.name} · Buy coins to resell
      </p>

      {message && (
        <p className={`mb-4 text-center text-sm font-medium ${
          message.startsWith("✅") ? "text-green-400" : "text-red-400"
        }`}>
          {message}
        </p>
      )}

      <form
        onSubmit={handleSubmit}
        className={`space-y-4 ${theme.cardBg} p-6 rounded-xl`}
      >
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
            {theme.currencySymbol}
          </span>
          <input
            type="number"
            name="amount"
            placeholder="Enter amount to purchase"
            value={form.amount}
            onChange={handleChange}
            required
            className={`w-full pl-9 pr-4 py-3 rounded-lg ${theme.inputBg} text-white`}
          />
        </div>

        <select
          name="source"
          value={form.source}
          onChange={handleChange}
          className={`w-full p-3 rounded-lg ${theme.inputBg} text-white`}
        >
          {theme.sources.map((src) => (
            <option key={src.value} value={src.value}>
              {src.label}
            </option>
          ))}
        </select>

        <button
          type="submit"
          disabled={loading}
          className={`w-full ${theme.submitBtn} py-3 rounded-lg font-semibold text-white transition disabled:opacity-60`}
        >
          {loading ? "Sending..." : "Submit Purchase Request"}
        </button>
      </form>

      {/* ✅ Show admin bank details */}
      {adminBank && (
        <div className={`mt-6 ${theme.cardBg} p-6 rounded-xl border ${theme.adminBoxAccent}`}>
          <h2 className="text-lg font-bold mb-3">
            {isGhana ? "Admin Payment Details" : "Admin Bank Details"}
          </h2>
          <div className="space-y-2 text-sm">
            <p>
              🏦 {isGhana ? "Provider" : "Bank"}: <span className="font-semibold">{adminBank.bankName}</span>
            </p>
            <div className="flex items-center justify-between">
              <p>
                💳 {isGhana ? "MoMo / Account" : "Account Number"}:{" "}
                <span className="font-semibold">{adminBank.accountNumber}</span>
              </p>
              <button
                onClick={() => copyToClipboard(adminBank.accountNumber)}
                className="ml-3 text-xs bg-white/10 px-3 py-1 rounded-lg hover:bg-white/20"
              >
                Copy
              </button>
            </div>
            <p>
              👤 Account Name: <span className="font-semibold">{adminBank.accountName}</span>
            </p>
            {form.amount && (
              <p className="mt-3 pt-3 border-t border-white/10">
                💰 Amount to send:{" "}
                <span className="text-yellow-400 font-bold text-base">
                  {theme.currencySymbol}{Number(form.amount).toLocaleString()}
                </span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorPurchasePage;
